/**
 * EditContactDataForm — headless form for editing employee contact/residence data.
 *
 * Used by:
 *   - EditContactDataDialog (legacy standalone wrapper, kept for backwards compat)
 *   - EmployeeProfileDialog "I miei dati" tab
 *
 * Props:
 *   - employee:        the employee record (used to seed the form)
 *   - onSaved(data):   called after successful save with the PATCH response
 *   - onClose():       called after submit completes successfully OR cancel
 *   - onOpenUpload():  hook to open the upload dialog (residence document path)
 */
/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, AlertTriangle, Upload } from "lucide-react";
import { toast } from "sonner";

export const EditContactDataForm = ({ employee, onSaved, onClose, onOpenUpload }) => {
    const [form, setForm] = useState({
        email: "", cell_personale: "", telefono_fisso: "",
        indirizzo: "", cap: "", citta: "", provincia: "", note: "",
    });
    const [original, setOriginal] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!employee) return;
        const base = {
            email: employee.email || "",
            cell_personale: employee.cell_personale || "",
            telefono_fisso: employee.telefono_fisso || "",
            indirizzo: employee.indirizzo || "",
            cap: employee.cap || "",
            citta: employee.citta || "",
            provincia: employee.provincia || "",
            note: "",
        };
        setForm(base);
        setOriginal(base);
    }, [employee]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const residenceFields = ["indirizzo", "cap", "citta", "provincia"];
    const residenceChanged = residenceFields.some((k) => (form[k] || "") !== (original[k] || ""));
    const anyChanged = Object.keys(form).some((k) => k !== "note" && (form[k] || "") !== (original[k] || ""));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { note: form.note || undefined };
            for (const k of Object.keys(form)) {
                if (k === "note") continue;
                if ((form[k] || "") !== (original[k] || "")) payload[k] = form[k] || null;
            }
            const { data } = await api.patch("/employees/me/contact", payload);
            if (data.status === "no_changes") {
                toast.info("Nessuna modifica da salvare");
            } else {
                toast.success(`Modifiche inviate: ${data.changes.length} campi aggiornati`);
                if (data.residence_changed) {
                    toast.warning("Ricorda di inviare il documento ufficiale di residenza", { duration: 4000 });
                }
                onSaved?.(data);
            }
            onClose?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore aggiornamento");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-data-form">
            {/* Contatti */}
            <section className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-widest text-[#376DB3]">Contatti</h3>
                <div>
                    <Label htmlFor="ec-email">Email</Label>
                    <Input id="ec-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="ec-email" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="ec-cell">Cellulare</Label>
                        <Input id="ec-cell" value={form.cell_personale} onChange={(e) => set("cell_personale", e.target.value)} className="mt-1" data-testid="ec-cell" />
                    </div>
                    <div>
                        <Label htmlFor="ec-tel">Telefono fisso</Label>
                        <Input id="ec-tel" value={form.telefono_fisso} onChange={(e) => set("telefono_fisso", e.target.value)} className="mt-1" data-testid="ec-tel" />
                    </div>
                </div>
            </section>

            {/* Residenza */}
            <section className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-widest text-[#376DB3]">Residenza</h3>
                <div>
                    <Label htmlFor="ec-ind">Indirizzo (via e numero civico)</Label>
                    <Input id="ec-ind" value={form.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} className="mt-1" data-testid="ec-indirizzo" />
                </div>
                <div className="grid grid-cols-[100px_1fr_80px] gap-3">
                    <div>
                        <Label htmlFor="ec-cap">CAP</Label>
                        <Input id="ec-cap" value={form.cap} onChange={(e) => set("cap", e.target.value)} className="mt-1" maxLength={5} data-testid="ec-cap" />
                    </div>
                    <div>
                        <Label htmlFor="ec-citta">Città</Label>
                        <Input id="ec-citta" value={form.citta} onChange={(e) => set("citta", e.target.value)} className="mt-1" data-testid="ec-citta" />
                    </div>
                    <div>
                        <Label htmlFor="ec-prov">Prov.</Label>
                        <Input id="ec-prov" value={form.provincia} onChange={(e) => set("provincia", e.target.value.toUpperCase())} className="mt-1" maxLength={2} data-testid="ec-prov" />
                    </div>
                </div>

                {residenceChanged && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="residence-warning">
                        <div className="flex items-start gap-2 text-amber-900">
                            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                            <div className="flex-1">
                                <div className="font-medium">Hai modificato la residenza</div>
                                <div className="mt-0.5 text-xs">
                                    Per validare il cambio di residenza è obbligatorio inviare il <b>documento ufficiale</b>
                                    (certificato di residenza, autocertificazione firmata, comunicazione del Comune…).
                                </div>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onOpenUpload?.()}
                                    className="mt-2 h-7 gap-1 border-amber-400 bg-white text-xs text-amber-800 hover:bg-amber-100"
                                    data-testid="open-upload-from-warning"
                                >
                                    <Upload className="h-3 w-3" />
                                    Carica documento di residenza
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            <div>
                <Label htmlFor="ec-note">Nota per HR (opzionale)</Label>
                <textarea
                    id="ec-note"
                    value={form.note}
                    onChange={(e) => set("note", e.target.value)}
                    rows={2}
                    className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-[#376DB3] focus:outline-none focus:ring-1 focus:ring-[#376DB3]"
                    placeholder="Es. 'Cambiato numero di cellulare dal 1° giugno'…"
                    data-testid="ec-note"
                />
            </div>

            <div className="flex justify-end gap-2 pt-2">
                {onClose && (
                    <Button type="button" variant="outline" onClick={onClose} data-testid="ec-cancel">Annulla</Button>
                )}
                <Button
                    type="submit"
                    disabled={submitting || !anyChanged}
                    className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                    data-testid="ec-submit"
                >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Salva modifiche
                </Button>
            </div>
        </form>
    );
};
